import { useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import { ModalProvider } from '@panwds/react-ui'
import { GlobalFilterProvider } from '@panwds/react-filters'
import { cache } from '../../framework/cache'
import { EMPTY_NSC_PAGE_FILTERS, NSC_PAGE_FILTERS_KEY, SCM_ONE_NAV_APP_NAME, SPARKY_FRAMEWORK } from '../../framework/constants'
import { authenticated, authorized, getFrameworkVar } from '../../framework/states'
import { stateRef, store } from '../../framework/store'
import { getExposedNamespace, getExposedSync } from '../../framework/microApps'
import { isFunction, isNonemptyArray } from '../../framework/utils'
import { isInSilentLoginIframe, tryParseJson } from '../../init'
import { __info } from '../../init/debug'
import BypassAuthProvider from '../auth/BypassOidcAuth'
import { OidcAuthProvider } from '../auth/OidcAuth'
import useLogout from '../auth/useLogout'
import useTsgErrorModal from '../auth/useTsgErrorModal'
import {
  useFawkesSession,
  useGlobalEventListener,
  useThemePreference,
  useTsgTenantInfo,
  useTsgTitleSync,
  useVarConfigEffect,
} from '../common/appEffects'
import { ErrorBoundary } from '../common/ErrorBoundary'
import reducer from '../common/reducer'
import {
  forceRender,
  microAppInitFailed,
  prerenderLeftNav,
  useRerender,
} from '../common/routeRender'
import { dynamicMicroAppsTsgLoad, resolveMicroApps } from '../common/routes'
import { GlobalStateContext, useStoreReducer } from '../common/storeHook'
import { getShellOverrideConfig } from './navUtils'
import Main from './Main'

import type { VFC, ReactElement } from 'react'
import type { match as Match } from 'react-router'
import type { Location } from 'history'
import type { AuthState, FrameworkVars, MainState, NSCFilters, StoreState } from '../../framework/stateTypes'
import type { InitedMicroAppConfig, NavAppAttr, NavItem, SparkySettings } from '../../framework/microAppTypes'

const DEFAULT_AUTH = { loading: true, initializing: true } as AuthState
const DEFAULT_LOCALE = 'en-US'

const AppContainer: VFC<{ settings: SparkySettings, match?: Match, location?: Location }> = ({ settings, match, location }): ReactElement => {
  const state = useStoreReducer(reducer, settings, (settings) => {
    const vars = (settings.variables[SPARKY_FRAMEWORK] || {}) as FrameworkVars
    const initState = {
      match,
      location,
      locationRoute: null,
      menuItemPath: null,
      activeItem: null,
      forceLogin: false,
      isSideNavCollapsed: false,
      cache,
      vars: settings.variables,
      fxVars: vars,
      exposes: settings.exposes,
      microAppNameSet: settings.microAppNameSet,
      leftMainNavigation: Object.assign([], { flattenNavigationList: [], pathAvailMap: {}, initializing: true }),
      leftMainActions: [],
      navRoutes: Object.defineProperties([], { loading: { value: true } }),
      loginFailed: undefined,
      ipRestrict: window.location.search.includes('ipRestrict=true'),
      menuData: [],
      [NSC_PAGE_FILTERS_KEY]: EMPTY_NSC_PAGE_FILTERS as NSCFilters,
      auth: DEFAULT_AUTH,
      main: { configLocation: {} } as MainState,
      preferences: {
        user: tryParseJson(sessionStorage.spk_user_pref) || {
          theme: localStorage.spk_user_theme || vars.default_user_theme
        }
      },
    }
    if (!isInSilentLoginIframe) {
      const microAppsInitPromise = authenticated.then(async () => {
        let microApps = settings.navigation as NavAppAttr[]
        if (vars.shell_path_overrides && !vars.private_site_origin && !vars.hub) {
          if (window.location.pathname === '/') {
            await authorized // wait for tsg info loaded to get default route redirection
          }
          const {
            path: shellPath,
            micro_app_ids,
            disable_micro_app_ids,
            '+variables': varsOverrides,
          } = getShellOverrideConfig()
          if (Array.isArray(micro_app_ids)) { // allow empty array
            __info(`init micro-apps ${micro_app_ids} only for path ${shellPath}`)
            microApps = microApps.filter((app) => micro_app_ids.includes(app.id!))
          }
          else if (isNonemptyArray(disable_micro_app_ids)) {
            __info(`disable micro-apps ${disable_micro_app_ids} for path ${shellPath}`)
            microApps = microApps.filter((app) => !disable_micro_app_ids.includes(app.id!))
          }
          if (varsOverrides && Object.keys(varsOverrides).length) {
            Object.assign(vars, varsOverrides) // TODO: support merging logic
          }
        }
        return resolveMicroApps(microApps)
      })
      const microAppEagerLoadPromise = vars.hub || vars.private_site_origin || vars.show_nav_content_only ? undefined : microAppsInitPromise.then(async (microApps) => {
        await authorized
        const { main, auth } = stateRef.current
        const cachePromise = vars.use_left_nav_cache ? cache.get<NavItem[]>(`left-nav-cache/${auth?.tsg_id}`) : undefined
        const loadedMicroApps = await dynamicMicroAppsTsgLoad({ main, auth, eargerLoad: true }, microApps.filter(app => {
          if (isFunction(app)) {
            return false
          }
          return isNonemptyArray((app as InitedMicroAppConfig).actions)
        }))
        const earlyActions = getExposedSync('sparky.navigation.early_init', 'actions')
        if (earlyActions) {
          loadedMicroApps.push({ id: SCM_ONE_NAV_APP_NAME, appName: SCM_ONE_NAV_APP_NAME, actions: earlyActions })
        }

        if (cachePromise) {
          Object.defineProperties(loadedMicroApps, {
            cached: { value: await cachePromise },
            icons: { value: getExposedNamespace('sparky.navigation.icons') || {} },
          })
        }
        return loadedMicroApps
      })
      const microAppsTsgLoadPromise = authorized.then(async () => dynamicMicroAppsTsgLoad(stateRef.current, await microAppsInitPromise))

      Object.assign(initState, {
        microAppsInitPromise,
        microAppsTsgLoadPromise,
        microAppEagerLoadPromise,
      })
    }
    else {
      __info('silent sign-in callback in iframe, skip init micro-apps')
    }
    return initState
  }) as StoreState
  useEffect(() => {
    if (state.microAppsTsgLoadPromise) {
      state.microAppsTsgLoadPromise.then(forceRender, microAppInitFailed)
    }
  }, [state.microAppsTsgLoadPromise, settings.exposes])
  useEffect(() => {
    if (state.microAppEagerLoadPromise) {
      state.microAppEagerLoadPromise.then(prerenderLeftNav, microAppInitFailed)
    }
  }, [state.microAppEagerLoadPromise])
  useGlobalEventListener('switch-tsg-completed', getFrameworkVar('msp') && !getFrameworkVar('hub') ? async ({ tsg_id }: Partial<AuthState>) => {
    __info('tsg switched without reload, run tsgLoad for micro-apps', tsg_id)
    dynamicMicroAppsTsgLoad(stateRef.current, await stateRef.current.microAppsInitPromise).then(forceRender, microAppInitFailed)
  } : undefined)

  useTsgTitleSync(state.auth)
  useTsgTenantInfo(state.auth)
  useLogout(state)
  useRerender()
  useVarConfigEffect()
  useThemePreference(state.themeOverride || state.preferences?.user?.theme)
  useFawkesSession(state.auth)

  const AuthProvider = getFrameworkVar('use_provided_tsg_token') ? BypassAuthProvider : OidcAuthProvider

  return (
    <ErrorBoundary context='app-main'>
      <GlobalStateContext.Provider value={store}>
        <IntlProvider defaultLocale={DEFAULT_LOCALE} locale={navigator.language}>
          <ModalProvider>
            <AuthProvider settings={settings}>
              <GlobalFilterProvider>
                <Main
                  state={state}
                  main={state.main}
                  oidc={state.oidc}
                  stateRef={stateRef}
                />
              </GlobalFilterProvider>
            </AuthProvider>
            {useTsgErrorModal()}
          </ModalProvider>
        </IntlProvider>
      </GlobalStateContext.Provider>
    </ErrorBoundary>
  )
}

export default AppContainer
